import { useState, useRef, useEffect } from "react";
import { debugLogger } from '@/utils/debugLogger';

export interface SimpleAudioRecording {
  blob: Blob;
  duration: number;
  url: string;
}

export const useSimpleAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioRecording, setAudioRecording] = useState<SimpleAudioRecording | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0); // Ref para capturar tempo de início
  
  // Preparar gravador
  const prepareRecorder = async (): Promise<boolean> => {
    try {
      debugLogger.debug('[SimpleAudioRecorder] Preparing recorder...');
      setError(null);
      
      // Parar streams existentes
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // Solicitar nova stream
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      streamRef.current = stream;
      
      // Verificar suporte para formatos
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          debugLogger.debug('[SimpleAudioRecorder] Data chunk received:', event.data.size);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        debugLogger.debug('[SimpleAudioRecorder] Recording stopped, creating blob...');
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          const audioUrl = URL.createObjectURL(audioBlob);
          
          // CORREÇÃO: Calcular duração real baseada no tempo transcorrido
          const actualDuration = startTimeRef.current > 0 
            ? Math.round((Date.now() - startTimeRef.current) / 1000)
            : recordingTime;
          
          debugLogger.debug('[SimpleAudioRecorder] Creating audio with duration:', actualDuration);
          
          setAudioRecording({
            blob: audioBlob,
            duration: actualDuration,
            url: audioUrl
          });
          
          debugLogger.debug('[SimpleAudioRecorder] Audio created:', {
            size: audioBlob.size,
            type: audioBlob.type,
            duration: actualDuration
          });
        }
      };
      
      mediaRecorderRef.current.onerror = (event) => {
        debugLogger.error('[SimpleAudioRecorder] Recording error:', event);
        setError('Erro durante a gravação');
        setIsRecording(false);
      };
      
      return true;
    } catch (error) {
      debugLogger.error('[SimpleAudioRecorder] Error preparing recorder:', error);
      setError('Erro ao acessar microfone. Verifique as permissões.');
      return false;
    }
  };
  
  // Iniciar gravação
  const startRecording = async (): Promise<boolean> => {
    debugLogger.debug('[SimpleAudioRecorder] Starting recording...');
    
    // Limpar gravação anterior
    setAudioRecording(null);
    setError(null);
    
    // Preparar gravador se necessário
    if (!mediaRecorderRef.current) {
      const prepared = await prepareRecorder();
      if (!prepared) return false;
    }
    
    // Resetar timer e chunks
    setRecordingTime(0);
    audioChunksRef.current = [];
    startTimeRef.current = Date.now(); // CORREÇÃO: Capturar tempo de início
    
    // Iniciar gravação
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'recording') {
      try {
        mediaRecorderRef.current.start(100); // Coletar dados a cada 100ms
        setIsRecording(true);
        
        // Iniciar timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        
        timerRef.current = window.setInterval(() => {
          setRecordingTime((prev) => prev + 1);
        }, 1000);
        
        debugLogger.debug('[SimpleAudioRecorder] Recording started successfully');
        return true;
      } catch (error) {
        debugLogger.error('[SimpleAudioRecorder] Error starting recording:', error);
        setError('Falha ao iniciar gravação');
        return false;
      }
    }
    
    return false;
  };
  
  // Parar gravação
  const stopRecording = (): boolean => {
    debugLogger.debug('[SimpleAudioRecorder] Stopping recording...');
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        
        // Parar timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        return true;
      } catch (error) {
        debugLogger.error('[SimpleAudioRecorder] Error stopping recording:', error);
        setError('Erro ao parar gravação');
        return false;
      }
    }
    
    return false;
  };
  
  // Cancelar gravação
  const cancelRecording = () => {
    debugLogger.debug('[SimpleAudioRecorder] Cancelling recording...');
    
    // Parar gravação se ativa
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try {
        mediaRecorderRef.current.stop();
      } catch (error) {
        debugLogger.error('[SimpleAudioRecorder] Error stopping during cancel:', error);
      }
    }
    
    // Parar timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // Resetar estados
    setIsRecording(false);
    setRecordingTime(0);
    audioChunksRef.current = [];
    startTimeRef.current = 0; // CORREÇÃO: Resetar tempo de início
    
    // CORREÇÃO: Limpar URL se existir antes de resetar audioRecording
    if (audioRecording?.url) {
      try {
        URL.revokeObjectURL(audioRecording.url);
        debugLogger.debug('[SimpleAudioRecorder] Revoked existing audio URL');
      } catch (error) {
        debugLogger.error('[SimpleAudioRecorder] Error revoking URL:', error);
      }
    }
    setAudioRecording(null);
    setError(null);
    
    // CORREÇÃO: Liberar microfone completamente
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        try {
          track.stop();
          debugLogger.debug('[SimpleAudioRecorder] Stopped track:', track.kind);
        } catch (error) {
          debugLogger.error('[SimpleAudioRecorder] Error stopping track:', error);
        }
      });
      streamRef.current = null;
      debugLogger.debug('[SimpleAudioRecorder] Stream completely released');
    }
    
    // Forçar recriação do gravador
    mediaRecorderRef.current = null;
    
    debugLogger.debug('[SimpleAudioRecorder] Complete cancellation finished');
  };
  
  // Formatar tempo
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Cleanup na desmontagem
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      if (audioRecording?.url) {
        URL.revokeObjectURL(audioRecording.url);
      }
    };
  }, []);
  
  return {
    isRecording,
    recordingTime,
    audioRecording,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
    formatTime
  };
};
